import React, { FC, useEffect, useRef, useState } from 'react';
import { FormInput, IFormInputProps } from '../merchants/FormInput';
import {
  ADYEN_CONFIG_INPUTS,
  BRAINTREE_CONFIG_INPUTS,
  CAPABILITIES_CHECKBOXES,
  CAPABILITIES_INPUTS,
  CHECKOUT_CONFIG_INPUTS,
  MAIN_PROVIDER_INPUTS,
  NMI_CONFIG_INPUTS,
  PAYSAFE_CONFIG_INPUTS,
  RAPYD_CONFIG_INPUTS,
  REVOLUT_CONFIG_INPUTS,
  STRIPE_CONFIG_INPUTS,
  USIO_CONFIG_INPUTS,
} from '../../constants/provider/provider-inputs';
import { useFormik } from 'formik';
import {
  PROVIDER,
  PROVIDER_FORM_FIELD_NAMES,
  PROVIDER_FORM_FIELDS,
} from '../../constants/provider/provider-form';
import { FormCheckbox } from '../application/FormCheckbox';
import { useTranslation } from 'react-i18next';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { Title } from '../developers/page-components/Title';
import clsx from 'clsx';
import { usePaymentContext } from '../../store/payments/payments-context';
import { fetchProviderLists } from '../../store/payments/actions/providers';
import { fetchMccList } from '../../store/merchnts/actions/merchant';

interface Props {
  selectedGateway: string;
  formik: ReturnType<typeof useFormik>;
}
export const AddNewProviderModalForm: FC<Props> = (props) => {
  const { selectedGateway, formik } = props;

  const cardBrandsRef = useRef(null);
  const countriesRef = useRef(null);
  const currenciesRef = useRef(null);
  const supportCurrencyRef = useRef(null);

  const { t } = useTranslation();
  const blockRef = useRef(null);
  const { merchantState: { whitelabelPartnersListSelector, merchantsLists, mccList }, dispatch: merchantDispatch } = useMerchants();
  const { paymentContextState: { providerLists }, dispatch } = usePaymentContext();

  const [hasOverflow, setHasOverflow] = useState<boolean>(false);

  const checkOverflow = (): void => {
    if (blockRef.current) {
      const scrollableHeight = blockRef.current.scrollHeight > blockRef.current.clientHeight;
      setHasOverflow(scrollableHeight);
    }
  };

  useEffect(() => {
    checkOverflow();
    const handleResize = (): void => checkOverflow();
    const blockCurrent = blockRef.current;

    blockCurrent.addEventListener('scroll', checkOverflow);
    window.addEventListener('resize', handleResize);

    return () => {
      blockCurrent.removeEventListener('scroll', checkOverflow);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (selectedGateway) {
      void fetchProviderLists({ dispatch, providerName: selectedGateway });
    }
  }, [selectedGateway]);

  useEffect(() => {
    void fetchMccList(merchantDispatch);
  }, []);

  const newCurrenciesList = providerLists?.CurrencyEnum
    ?.map(currency => merchantsLists?.currencies?.find(merchantCurrency => merchantCurrency.value === currency.value) || currency);

  const newCountriesList = providerLists?.Countries
    ?.map(country => merchantsLists?.countries?.find(merchantCountry => merchantCountry.value === country.value) || country);

  const MAIN_INPUTS = MAIN_PROVIDER_INPUTS({
    t,
    whiteLabelPartnersList: whitelabelPartnersListSelector,
    processorPlatformList: providerLists?.ProcessorPlatforms,
    mccList
  });

  const CAPABILITIES= CAPABILITIES_INPUTS({
    t,
    cardBrandsRef,
    countriesRef,
    currenciesRef,
    cardBrandsList: providerLists?.CardBrand,
    countriesList: newCountriesList,
    currenciesList: newCurrenciesList,
  });

  const CONFIG_INPUTS = (): IFormInputProps[] => {
    switch (selectedGateway.toLowerCase()) {
      case PROVIDER.CHECKOUT: return CHECKOUT_CONFIG_INPUTS({ t });
      case PROVIDER.STRIPE: return STRIPE_CONFIG_INPUTS({ t });
      case PROVIDER.ADYEN: return ADYEN_CONFIG_INPUTS({ t });
      case PROVIDER.BRAINTREE: return BRAINTREE_CONFIG_INPUTS({ t });
      case PROVIDER.NMI: return NMI_CONFIG_INPUTS({ t });
      case PROVIDER.PAYSAFE: return PAYSAFE_CONFIG_INPUTS({ t });
      case PROVIDER.REVOLV3: return REVOLUT_CONFIG_INPUTS({ t, processorList: providerLists?.Revolv3Processor });
      case PROVIDER.RAPYD: return RAPYD_CONFIG_INPUTS({
        t,
        currenciesList: providerLists?.CurrencyEnum,
        supportCurrencyRef
      });
      case PROVIDER.USIO: return USIO_CONFIG_INPUTS({ t });
      default: return [];
    }
  };

  return (
    <form className="ps-8 me-4 row position-relative" onSubmit={formik.handleSubmit}>
      <div className={clsx(hasOverflow && 'side-modal-box-shadow')} ref={blockRef} >
        <div className="row">
          {
            MAIN_INPUTS.map(item => (
              <FormInput
                {...item}
                formik={formik}
                key={item.key}
              />
            ))
          }
        </div>

        <Title title="Configurations" additionalClass="mb-5 fs-4 fw-bolder" />

        <div className="row">
          {
            CONFIG_INPUTS().map(item => (
              <FormInput
                {...item}
                formik={formik}
                key={item.key}
              />
            ))
          }
        </div>

        <Title title="Capabilities" additionalClass="mb-5 fs-4 fw-bolder" />

        <div className="row">
          {
            CAPABILITIES.map(item => (
              <FormInput
                {...item}
                formik={formik}
                key={item.key}
              />
            ))
          }
          <div className="col-3"/>
          {
            CAPABILITIES_CHECKBOXES.map(({ label, fieldName }) => (
              <FormCheckbox
                formik={formik}
                fieldName={fieldName}
                label={label}
                key={fieldName}
                additionalClasses="mt-5"
              />
            ))
          }
        </div>

        <div className="separator py-5"/>

        <div className="row mb-5">
          <FormCheckbox
            formik={formik}
            fieldName={PROVIDER_FORM_FIELDS.IS_ACTIVE}
            label={PROVIDER_FORM_FIELD_NAMES.IS_ACTIVE}
            additionalClasses="mt-5"
          />
          <FormCheckbox
            formik={formik}
            fieldName={PROVIDER_FORM_FIELDS.IS_SANDBOX}
            label={PROVIDER_FORM_FIELD_NAMES.IS_SANDBOX}
            additionalClasses="mt-5"
          />
        </div>
      </div>
    </form>
  );
};
